import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import { SITE_NAME } from '../constants/site';
import QuizShareCard from '../components/QuizShareCard';
import PersonalProfileCard from '../components/PersonalProfileCard';

export default function CardsGalleryPage() {
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const [currentUserIdx, setCurrentUserIdx] = useState(0);
  const [currentCardIdx, setCurrentCardIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [likedCards, setLikedCards] = useState<Set<string>>(new Set());

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentRecordId, setCurrentRecordId] = useState<string | null>(null);

  const touchStart = useRef({ x: 0, y: 0 });
  const lastTap = useRef(0);

  useEffect(() => {
    try {
      const rid = localStorage.getItem('nagiDatingRecordId');
      if (rid) {
        setIsLoggedIn(true);
        setCurrentRecordId(rid);
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('nagiLikedCards');
      if (stored) {
        setLikedCards(new Set(JSON.parse(stored)));
      }
    } catch {}
  }, []);

  const saveLiked = (newSet: Set<string>) => {
    setLikedCards(newSet);
    try {
      localStorage.setItem('nagiLikedCards', JSON.stringify(Array.from(newSet)));
    } catch {}
  };

  const fetchCards = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/public/cards?sort=random&limit=150`);
      const data = await res.json();
      if (data.success && data.cards && data.cards.length > 0) {
        const groupsMap = new Map();
        for (const card of data.cards) {
          const key = card.record_id || 'guest';
          if (!groupsMap.has(key)) {
            groupsMap.set(key, {
              user: card.user || {},
              cards: [],
              record_id: card.record_id
            });
          }
          groupsMap.get(key).cards.push(card);
        }
        let groups = Array.from(groupsMap.values());
        groups.sort(() => Math.random() - 0.5);
        const typeOrder: { [key: string]: number } = { personality: 0, single: 1, bedroom: 2 };
        groups.forEach((g: any) => {
          g.cards.sort((a: any, b: any) => (typeOrder[a.quiz_type] ?? 3) - (typeOrder[b.quiz_type] ?? 3));
        });

        // Inject a dedicated "personal profile card" as the first card for every user.
        // This fulfills the request for a visible "thẻ cá nhân" with nickname, role, job, physical stats, zodiac, Gu, tìm kiếm, avatar.
        groups.forEach((g: any) => {
          if (!g.cards || g.cards.length === 0) return;
          const u = g.user || g.cards[0]?.user || {};
          // Try to pull Gu info from an existing personality card in the group (if present)
          const pers = g.cards.find((c: any) => c.quiz_type === 'personality');
          const profileCard = {
            id: `profile_${g.record_id || 'unknown'}`,
            quiz_type: 'profile',
            aspect_ratio: 'story',
            synthetic: true,
            display_name: u.nickname || g.cards[0]?.display_name,
            user: u,
            // Rich profile data for the new card component
            profileData: {
              nickname: u.nickname,
              city: u.city,
              height: u.height,
              weight: u.weight,
              bodyType: u.bodyType || u.body_type,
              job: u.job,
              role: u.role,
              zodiac: u.zodiac,
              rel: u.rel,
              guTitle: pers?.result?.shareTitle || pers?.share_title,
              guHook: pers?.result?.shareHook || '',
            },
          };
          g.cards.unshift(profileCard);
        });

        setUserGroups(groups);
        setCurrentUserIdx(0);
        setCurrentCardIdx(0);
      } else {
        setUserGroups([]);
      }
    } catch {
      setUserGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCards();
  }, []);

  const currentGroup = userGroups[currentUserIdx];
  const currentCard = currentGroup ? currentGroup.cards[currentCardIdx] : null;

  const goNextUser = () => {
    if (currentUserIdx < userGroups.length - 1) {
      setCurrentUserIdx(currentUserIdx + 1);
      setCurrentCardIdx(0);
    }
  };

  const goPrevUser = () => {
    if (currentUserIdx > 0) {
      setCurrentUserIdx(currentUserIdx - 1);
      setCurrentCardIdx(0);
    }
  };

  const goNextCard = () => {
    if (currentGroup && currentCardIdx < currentGroup.cards.length - 1) {
      setCurrentCardIdx(currentCardIdx + 1);
    }
  };

  const goPrevCard = () => {
    if (currentCardIdx > 0) {
      setCurrentCardIdx(currentCardIdx - 1);
    }
  };

  const handleDoubleTapLike = () => {
    if (!currentCard || likedCards.has(currentCard.id)) return;

    const targetCardId = currentCard.id;
    const isThim = isLoggedIn && currentRecordId;
    const isSynthetic = !!(currentCard.synthetic || (targetCardId && String(targetCardId).startsWith('syn_')));

    // Optimistic UI bump for the specific card (by id, not by potentially stale index)
    setUserGroups(prev => {
      return prev.map((group: any) => {
        const cardIdx = group.cards.findIndex((c: any) => c && c.id === targetCardId);
        if (cardIdx === -1) return group;

        const newCards = [...group.cards];
        const oldC = newCards[cardIdx];
        const newC = { ...oldC };
        newC.total = (newC.total || 0) + 1;
        if (isThim) {
          newC.thims = (newC.thims || 0) + 1;
        } else {
          newC.likes = (newC.likes || 0) + 1;
        }
        newCards[cardIdx] = newC;
        return { ...group, cards: newCards };
      });
    });

    const newLiked = new Set(likedCards);
    newLiked.add(targetCardId);
    saveLiked(newLiked);

    if (isSynthetic) {
      // Samples / synthetic cards: likes are client-only demo (no real DB row).
      // Count bump already applied above; do not call API to avoid 404 noise.
      return;
    }

    const payload: any = { cardId: targetCardId };
    if (isThim && currentRecordId) payload.likerRecordId = currentRecordId;

    fetch('/api/public/like-card', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then((res) => res.json().catch(() => ({})))
      .then((data: any) => {
        if (data && data.success && typeof data.total === 'number') {
          // Sync authoritative counts from server (in case of concurrent likes etc.)
          setUserGroups((prev) => {
            return prev.map((group: any) => {
              const cardIdx = group.cards.findIndex((c: any) => c && c.id === targetCardId);
              if (cardIdx === -1) return group;
              const newCards = [...group.cards];
              const oldC = newCards[cardIdx];
              const newC = {
                ...oldC,
                likes: typeof data.likes === 'number' ? data.likes : oldC.likes,
                thims: typeof data.thims === 'number' ? data.thims : oldC.thims,
                total: data.total,
              };
              newCards[cardIdx] = newC;
              return { ...group, cards: newCards };
            });
          });
        } else if (data && !data.success) {
          // Server rejected (e.g. card gone) — revert the bump and the liked flag for real cards
          setUserGroups((prev) => {
            return prev.map((group: any) => {
              const cardIdx = group.cards.findIndex((c: any) => c && c.id === targetCardId);
              if (cardIdx === -1) return group;
              const newCards = [...group.cards];
              const oldC = newCards[cardIdx];
              const newC = { ...oldC };
              newC.total = Math.max(0, (newC.total || 0) - 1);
              if (isThim) {
                newC.thims = Math.max(0, (newC.thims || 0) - 1);
              } else {
                newC.likes = Math.max(0, (newC.likes || 0) - 1);
              }
              newCards[cardIdx] = newC;
              return { ...group, cards: newCards };
            });
          });
          const rev = new Set(likedCards);
          rev.delete(targetCardId);
          saveLiked(rev);
        }
      })
      .catch(() => {
        // Network error: revert count bump + liked flag (for real cards)
        setUserGroups((prev) => {
          return prev.map((group: any) => {
            const cardIdx = group.cards.findIndex((c: any) => c && c.id === targetCardId);
            if (cardIdx === -1) return group;
            const newCards = [...group.cards];
            const oldC = newCards[cardIdx];
            const newC = { ...oldC };
            newC.total = Math.max(0, (newC.total || 0) - 1);
            if (isThim) {
              newC.thims = Math.max(0, (newC.thims || 0) - 1);
            } else {
              newC.likes = Math.max(0, (newC.likes || 0) - 1);
            }
            newCards[cardIdx] = newC;
            return { ...group, cards: newCards };
          });
        });
        const rev = new Set(likedCards);
        rev.delete(targetCardId);
        saveLiked(rev);
      });
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const t = e.changedTouches[0];
    const start = touchStart.current;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const adx = Math.abs(dx);
    const ady = Math.abs(dy);
    const now = Date.now();

    if (adx < 35 && ady < 35) {
      if (now - lastTap.current < 280) {
        handleDoubleTapLike();
        lastTap.current = 0;
      } else {
        lastTap.current = now;
      }
    } else if (ady > adx && ady > 55) {
      if (dy < 0) goNextUser();
      else goPrevUser();
    } else if (adx > 55) {
      if (dx < 0) goNextCard();
      else goPrevCard();
    }
  };

  const handleDoubleClick = () => {
    handleDoubleTapLike();
  };

  const renderCurrentCard = () => {
    if (!currentGroup || !currentCard) return null;

    const isStory = currentCard.aspect_ratio === 'story';
    const nick = currentGroup.user?.nickname || currentCard.display_name || 'Ẩn danh';
    const total = currentCard.total ?? ((currentCard.likes || 0) + (currentCard.thims || 0));
    const isLiked = likedCards.has(currentCard.id);
    const isProfileCard = currentCard.quiz_type === 'profile' || (currentCard.id && String(currentCard.id).startsWith('profile_'));

    const wrapperStyle = {
      width: '100%',
      maxWidth: 420,
      margin: '0 auto',
      aspectRatio: isStory ? '9/16' : '3/4',
      position: 'relative',
      borderRadius: 18,
      overflow: 'hidden',
      boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
      background: '#0a0a0a',
      touchAction: 'none'
    } as const;

    if (isProfileCard) {
      // Dedicated personal profile card (thẻ cá nhân) - first in each user's horizontal stack
      return (
        <div
          style={wrapperStyle}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onDoubleClick={handleDoubleClick}
        >
          <PersonalProfileCard
            data={currentCard.profileData || currentGroup.user}
            displayName={nick}
            totalLikes={total}
            isLiked={isLiked}
            onDoubleTap={handleDoubleTapLike}
          />

          {/* Overlays consistent with quiz cards */}
          <div style={{ position: 'absolute', top: 14, left: 14, right: 14, display: 'flex', justifyContent: 'space-between', pointerEvents: 'none' }}>
            <div style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '3px 11px', borderRadius: 999, fontSize: '0.82rem' }}>
              Hồ sơ
            </div>
            <div
              style={{ background: isLiked ? 'rgba(251,113,133,0.92)' : 'rgba(0,0,0,0.6)', color: '#fff', padding: '3px 10px', borderRadius: 999, fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 4, pointerEvents: 'auto' }}
              onClick={(e) => { e.stopPropagation(); handleDoubleTapLike(); }}
            >
              {isLoggedIn ? '💛' : '❤️'} {total}
            </div>
          </div>
        </div>
      );
    }

    const hasCssCard = !!(currentCard.result || currentCard.synthetic);

    return (
      <div
        style={wrapperStyle}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onDoubleClick={handleDoubleClick}
      >
        {hasCssCard ? (
          <QuizShareCard
            id={`swipe-${currentCard.id}`}
            variant={currentCard.quiz_type as any}
            aspect={isStory ? 'story' : 'feed'}
            fitContainer
            displayWidth={380}
            shareTitle={currentCard.result?.shareTitle || currentCard.share_title || ''}
            shareHook={currentCard.result?.shareHook || ''}
            percent={currentCard.result?.percent || 60}
            typeKey={currentCard.result?.typeKey || 'A'}
            variantKey={currentCard.result?.variantKey || currentCard.variant_key}
            displayName={nick}
            city={currentGroup.user?.city}
            zodiac={currentGroup.user?.zodiac?.name}
            zodiacSymbol={currentGroup.user?.zodiac?.symbol}
            stats={currentCard.result?.stats || []}
            progressBars={currentCard.result?.progressBars}
          />
        ) : (
          <img
            src={currentCard.image_url}
            alt={currentCard.share_title || ''}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}

        <div style={{ position: 'absolute', top: 14, left: 14, right: 14, display: 'flex', justifyContent: 'space-between', pointerEvents: 'none' }}>
          <div style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '3px 11px', borderRadius: 999, fontSize: '0.82rem' }}>
            {nick}
          </div>
          <div
            style={{ background: isLiked ? 'rgba(251,113,133,0.92)' : 'rgba(0,0,0,0.6)', color: '#fff', padding: '3px 10px', borderRadius: 999, fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 4, pointerEvents: 'auto' }}
            onClick={(e) => { e.stopPropagation(); handleDoubleTapLike(); }}
          >
            {isLoggedIn ? '💛' : '❤️'} {total}
          </div>
        </div>

        {currentGroup.cards.length > 1 && (
          <div style={{ position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.55)', color: '#fff', padding: '1px 9px', borderRadius: 999, fontSize: '0.7rem' }}>
            {currentCardIdx + 1} / {currentGroup.cards.length}
          </div>
        )}
      </div>
    );
  };

  const currGroup = userGroups[currentUserIdx];

  return (
    <div className="app-container public-cards-page" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#000' }}>
      <SiteHeader>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <div className="logo" style={{ fontSize: '1.05rem', letterSpacing: '0.1em' }}>{SITE_NAME}</div>
          <div style={{ fontSize: '0.68rem', opacity: 0.6 }}>Thẻ card ngẫu nhiên</div>
        </div>
      </SiteHeader>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '8px 10px 70px', maxWidth: 460, margin: '0 auto', width: '100%' }}>
        {loading && (
          <div style={{ textAlign: 'center', paddingTop: 140, color: '#888' }}>Đang tải thẻ ngẫu nhiên...</div>
        )}

        {!loading && userGroups.length === 0 && (
          <div style={{ textAlign: 'center', paddingTop: 100 }}>
            <p style={{ color: '#888' }}>Chưa có thẻ card.</p>
            <Link to="/quiz" className="primary-btn" style={{ marginTop: 16, display: 'inline-block' }}>Bắt đầu làm quiz</Link>
          </div>
        )}

        {!loading && userGroups.length > 0 && currGroup && (
          <>
            <div style={{ textAlign: 'center', margin: '4px 0 10px' }}>
              <div style={{ fontSize: '1.0rem', fontWeight: 600, color: '#fff' }}>
                {currGroup.user?.nickname || 'Người dùng'}
              </div>
              <div style={{ fontSize: '0.68rem', color: '#777', marginTop: 2 }}>
                {currentUserIdx + 1} / {userGroups.length} users &nbsp;•&nbsp; Kéo ↑↓ đổi user • ←→ đổi thẻ (Hồ sơ / Quiz) • Chạm đúp để thả tim
              </div>
            </div>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {renderCurrentCard()}
            </div>
          </>
        )}
      </div>

      <div style={{ textAlign: 'center', padding: '12px 0 18px', fontSize: '0.72rem', color: '#555' }}>
        <Link to="/quiz" style={{ color: '#888' }}>✨ Tạo thẻ của bạn</Link>
      </div>
    </div>
  );
}
